import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from './hooks/redux-hooks';
import { map } from 'lodash';
import HomepagePlaceholder from './components/HomepagePlaceholder';
import { AISearchBox } from './components/searchbox/AISearchBox';
import ChatMsg from './components/ChatMsg';
import { ToastType } from './types/common.types';
import {
  setAgentInfo,
  setQuestions,
  setShadowColor,
  setTextColor,
  setLinkColor,
  setThreadId,
  setToastInfo,
  setUserInfo,
} from './store/common.slice';
import AgentService from './services/agent.service';
import { TAssistant } from '@answerr/types';
import useStorage from './hooks/useStorage';
import Header from './components/Header';
import Navbar from './components/Navbar';
import { setChatData } from './store/chat.slice';

// ============================================================
// CONSTANTS
// ============================================================
const MAPLE_LMS_BASE_URL = 'dannemking.v4.maplelms.com';
const SSO_TIMEOUT_MS = 60000;
const MAX_SSO_RETRIES = 1;

// ============================================================
// UTILITY FUNCTIONS (outside component to avoid re-creation)
// ============================================================
function getFirstSubdomainFromHostname(hostname: string): string | null {
  const host = hostname.startsWith('www.') ? hostname.slice(4) : hostname;
  const parts = host.split('.');
  return parts.length >= 3 ? parts[0] : null;
}

function getUrlParameter(name: string): string | null {
  return new URLSearchParams(window.location.search).get(name);
}

function removeUrlParameter(name: string): void {
  const url = new URL(window.location.href);
  url.searchParams.delete(name);
  window.history.replaceState({}, document.title, url.toString());
}

function needsMapleSSO(subdomain: string): boolean {
  if (!subdomain) return false;
  const lower = subdomain.toLowerCase();
  return lower.includes('-dmk') || lower.includes('dmk-');
}

// ============================================================
// COMPONENT
// ============================================================
function App() {
  // Redux
  const dispatch = useAppDispatch();
  const { chatData } = useAppSelector((state) => state.chat);
  const { agentInfo, shadowColor, showSidebar, userInfo } = useAppSelector(
    (state) => state.common
  );
  // Storage
  const { setItem, getItem } = useStorage();

  // Refs
  const chatWindowRef = useRef<HTMLDivElement | null>(null);
  const isInitialized = useRef(false);

  // UI State
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentError, setAgentError] = useState(false);
  const [ssoError, setSsoError] = useState<string | null>(null);

  // Subdomain (computed synchronously on initial render)
  const [subDomain] = useState<string>(() => {
    const hostname = window.location.hostname;
    return getFirstSubdomainFromHostname(hostname) || '';
  });

  // const subDomain = 'science-assistant';

  // Derived: does this subdomain require SSO?
  const requiresSSO = useMemo(() => needsMapleSSO(subDomain), [subDomain]);

  // MapleLMS SSO State
  const [isLMSAuthenticated, setIsLMSAuthenticated] = useState(
    () => getItem('isLMSAuthenticated') === 'true'
  );
  const [ssoAttempted, setSsoAttempted] = useState(
    () => getItem('hasMapleAuthAttempted') === 'true'
  );
  const [ssoRetryCount, setSsoRetryCount] = useState(() =>
    Number(getItem('mapleRetryCount') || '0')
  );

  // ============================================================
  // CALLBACKS (stable references)
  // ============================================================

  const showToast = useCallback(
    (message: string, type: ToastType) => {
      dispatch(setToastInfo({ isOpen: true, message, type }));
    },
    [dispatch]
  );

  const redirectToMapleLMSLogin = useCallback(() => {
    const loginUrl = `https://${MAPLE_LMS_BASE_URL}/local/mlib/session.php?wantsurl=${encodeURIComponent(window.location.origin)}`;
    console.log('MapleLMS SSO - Redirecting to:', loginUrl);
    window.location.href = loginUrl;
  }, []);

  const handleMapleLMS = useCallback(
    async (token: string, subdomain: string): Promise<boolean> => {
      try {
        const response = await AgentService.validateMapleLMSToken(
          token,
          subdomain
        );

        if (response.status === 401 || response.data?.status === 401) {
          console.error('MapleLMS SSO - 401 Unauthorized');
          removeUrlParameter('token');
          setSsoError('Authentication failed. Please log in again.');
          setIsLMSAuthenticated(false);
          setItem('isLMSAuthenticated', 'false', 'local');
          return false;
        }

        if (response.data && !response.data.error) {
          const userData = response.data.data;
          console.log('MapleLMS SSO - User authenticated:', userData);

          setIsLMSAuthenticated(true);
          setItem('isLMSAuthenticated', 'true', 'local');
          setSsoAttempted(false);
          setItem('hasMapleAuthAttempted', 'false', 'local');
          removeUrlParameter('token');
          dispatch(setUserInfo(userData.user));
          const name =
            userData.user?.firstName || userData.user?.email || 'User';
          showToast(`Welcome, ${name}!`, ToastType.SUCCESS);
          return true;
        } else {
          console.error('MapleLMS SSO - Auth failed:', response.data?.message);
          removeUrlParameter('token');
          setSsoError('You are not authorized to access this assistant.');
          setIsLMSAuthenticated(false);
          setItem('isLMSAuthenticated', 'false', 'local');
          setItem('hasMapleAuthAttempted', 'true', 'local');
          return false;
        }
      } catch (error) {
        console.error('MapleLMS SSO - Error:', error);
        removeUrlParameter('token');
        setSsoError('Authentication error. Please try again later.');
        setIsLMSAuthenticated(false);
        setItem('isLMSAuthenticated', 'false', 'local');
        setItem('hasMapleAuthAttempted', 'true', 'local');
        return false;
      }
    },
    [setItem, showToast]
  );

  const retryMapleLMSLogin = useCallback(() => {
    setSsoAttempted(false);
    setItem('hasMapleAuthAttempted', 'false', 'local');
    const newCount = ssoRetryCount + 1;
    setSsoRetryCount(newCount);
    setItem('mapleRetryCount', String(newCount), 'local');
    redirectToMapleLMSLogin();
  }, [redirectToMapleLMSLogin, setItem, ssoRetryCount]);

  const fetchAgent = useCallback(
    async (subdomain: string) => {
      console.log('Fetching agent for subdomain:', subdomain);

      if (!subdomain) {
        setAgentError(true);
        return;
      }

      try {
        setAgentLoading(true);
        const response = await AgentService.getAgent(subdomain);

        if (response.data.data) {
          const agent: TAssistant | null = response.data.data.agent;
          setAgentError(false);
          dispatch(setAgentInfo(agent));
          setItem('agentInfo', JSON.stringify(agent), 'local');
          document.title = agent?.name || 'Public Assistant';

          // Set favicon with org company logo
          const faviconLink = document.querySelector(
            "link[rel='icon']"
          ) as HTMLLinkElement;
          const appleFavLink = document.querySelector(
            "link[rel='apple-touch-icon']"
          ) as HTMLLinkElement;
          if (faviconLink && agent?.createdByOrg.companyLogo) {
            faviconLink.href = agent.createdByOrg.companyLogo;
          }
          if (appleFavLink && agent?.createdByOrg.companyLogo) {
            appleFavLink.href = agent.createdByOrg.companyLogo;
          }

          dispatch(setShadowColor(agent?.publicDetails?.bgColor || ''));
          dispatch(setTextColor(agent?.publicDetails?.textColor || ''));
          dispatch(setLinkColor(agent?.publicDetails?.linkColor || ''));
          dispatch(setQuestions(agent?.publicDetails?.exampleQuestions || []));
        } else {
          setAgentError(true);
          showToast('Error in fetching agent details.', ToastType.ERROR);
        }
      } catch (err) {
        console.error('fetchAgent error:', err);
        setAgentError(true);
        showToast('Error in fetching agent details.', ToastType.ERROR);
      } finally {
        setAgentLoading(false);
      }
    },
    [dispatch, setItem, showToast]
  );

  const initThread = useCallback(async (): Promise<boolean> => {
    const existingThread = getItem('threadId');
    const existingChatData = JSON.parse(getItem('chatData') || '[]');
    if (existingThread && existingChatData && existingChatData.length > 0) {
      dispatch(setThreadId(existingThread));
      dispatch(setChatData(existingChatData));
      return true;
    }

    try {
      const response = await AgentService.createThread();
      if (response.data.data) {
        dispatch(setThreadId(response.data.data));
        setItem('threadId', response.data.data);
        return true;
      } else {
        console.warn('Thread creation failed: no data');
        showToast(
          'Error in creating new thread. Please refresh the page.',
          ToastType.ERROR
        );
        return false;
      }
    } catch (err) {
      console.error('Thread creation error:', err);
      showToast(
        'Error in creating new thread. Please refresh the page.',
        ToastType.ERROR
      );
      return false;
    }
  }, [dispatch, getItem, setItem, showToast]);

  const scrollToBottom = useCallback(() => {
    chatWindowRef.current?.scrollTo({
      top: chatWindowRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, []);

  // ============================================================
  // MAIN INITIALIZATION EFFECT
  // Handles the sequential flow:
  // - Normal users: Thread -> FetchAgent
  // - DMK users: Thread -> SSO -> FetchAgent
  // ============================================================
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    const initialize = async () => {
      // Validate subdomain exists
      if (!subDomain) {
        console.error('No subdomain found');
        setAgentError(true);
        return;
      }

      console.log(
        'Initializing for subdomain:',
        subDomain,
        '| Requires SSO:',
        requiresSSO
      );

      // STEP 1: Initialize thread
      console.log('Step 1: Creating thread...');
      const threadSuccess = await initThread();
      if (!threadSuccess) {
        console.error('Thread initialization failed');
        return;
      }
      console.log('Step 1: Thread ready');

      // STEP 2: Handle SSO for DMK users
      if (requiresSSO) {
        console.log('Step 2: SSO required for DMK user');

        // Check if already authenticated
        if (isLMSAuthenticated) {
          console.log('Step 2: Already authenticated, skipping SSO');
        } else {
          const token = getUrlParameter('token');

          if (token) {
            // Token present - validate it
            console.log('Step 2: Token found, validating...');
            const ssoSuccess = await handleMapleLMS(token, subDomain);
            if (!ssoSuccess) {
              console.error('SSO validation failed');
              return; // Stop here, error UI will show
            }
            console.log('Step 2: SSO validation successful');
          } else if (!ssoAttempted) {
            // No token and no previous attempt - redirect to login
            console.log('Step 2: No token, redirecting to MapleLMS login...');
            setSsoAttempted(true);
            setItem('hasMapleAuthAttempted', 'true', 'local');
            redirectToMapleLMSLogin();
            return; // Stop here, will continue after redirect
          } else {
            // SSO was attempted but failed (no token returned)
            console.log('Step 2: SSO previously attempted but no token');
            setSsoError(
              'Authentication required. Please log in to access this assistant.'
            );
            return;
          }
        }
      } else {
        console.log('Step 2: SSO not required (normal user)');
      }

      // STEP 3: Fetch agent
      console.log('Step 3: Fetching agent...');
      await fetchAgent(subDomain);
    };

    initialize();
  }, [
    subDomain,
    requiresSSO,
    isLMSAuthenticated,
    ssoAttempted,
    initThread,
    handleMapleLMS,
    fetchAgent,
    redirectToMapleLMSLogin,
    setItem,
  ]);

  // SSO timeout with retry (only for DMK users waiting for SSO)
  useEffect(() => {
    if (!requiresSSO || isLMSAuthenticated || !ssoAttempted) return;

    const timeoutId = setTimeout(() => {
      if (ssoRetryCount < MAX_SSO_RETRIES) {
        showToast('SSO timed out. Retrying login...', ToastType.INFO);
        retryMapleLMSLogin();
      } else {
        setSsoError(
          'SSO timed out. Please refresh the page or contact support.'
        );
      }
    }, SSO_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [
    requiresSSO,
    isLMSAuthenticated,
    ssoAttempted,
    ssoRetryCount,
    retryMapleLMSLogin,
    showToast,
  ]);

  // Auto-scroll on new chat message
  useEffect(() => {
    const lastMsg = chatData[chatData.length - 1];
    if (chatData.length > 0 && lastMsg?.ques && !lastMsg?.answer) {
      scrollToBottom();
    }
  }, [chatData, scrollToBottom]);

  // ============================================================
  // RENDER
  // ============================================================

  if (ssoError) {
    return (
      <div className="w-[100vw] h-[100vh] p-5 justify-center items-center flex">
        <div className="max-w-[500px]">
          <h4 className="text-[22px] font-medium text-center mb-2 text-red-600">
            Authentication Failed
          </h4>
          <p className="text-[18px] font-medium text-center mb-4">{ssoError}</p>
          <p className="text-[16px] text-center text-gray-600 mb-4">
            Please contact your administrator or try logging in again.
          </p>
          <div className="flex justify-center">
            <button
              onClick={() => {
                // Clear all SSO-related state and storage
                setItem('hasMapleAuthAttempted', 'false', 'local');
                setItem('mapleRetryCount', '0', 'local');
                setItem('isLMSAuthenticated', 'false', 'local');
                setSsoError(null);
                setSsoAttempted(false);
                setSsoRetryCount(0);
                // Reset initialization flag and reload to start fresh
                isInitialized.current = false;
                window.location.reload();
              }}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (agentError) {
    return (
      <div className="w-[100vw] h-[100vh] p-5 justify-center items-center flex">
        <div className="max-w-[500px]">
          <h4 className="text-[22px] font-medium text-center mb-2">
            Oops! Something went wrong.
          </h4>
          <p className="text-[18px] font-medium text-center mb-2">
            We couldn't load this public agent page.
          </p>
          <p className="text-[18px] font-medium text-center">
            Please try again later or check if the link is correct.
          </p>
        </div>
      </div>
    );
  }

  if (agentLoading) {
    return (
      <div className="fixed w-full h-full left-0 top-0 z-[50] bg-[rgba(255,255,255,.7)] justify-center items-center flex">
        <span>Loading Assistant Info...</span>
      </div>
    );
  }

  return (
    <div
      className="flex justify-center grow h-[calc(100%-60px)] sm:h-full sm:pb-[40px] relative outline-none"
      style={{ background: shadowColor || '#f5f5f5' }}
    >
      <div className="flex flex-col grow justify-center w-full">
        <Header />
        {showSidebar && userInfo && <Navbar />}
        <div
          className={`overflow-y-auto flex transition-all ${chatData.length > 0 ? 'flex-grow' : ''}`}
          ref={chatWindowRef}
        >
          <div className="flex flex-col px-1 sm:px-5 mx-auto w-full sm:w-[400px] md:w-[500px] lg:w-[700px] xl:w-[900px] 2xl:w-[1000px]">
            {chatData.length > 0 ? (
              <div className="pb-2">
                {map(chatData, (item, index) => (
                  <div
                    className={`w-full last:mb-5 sm:last:mb-7 ${
                      index === chatData.length - 1
                        ? 'min-h-[calc(100vh-220px)]'
                        : ''
                    }`}
                    key={`${item.msgId}-ques-${index}`}
                  >
                    <ChatMsg
                      ques={item.ques}
                      answer={item.answer}
                      msgId={item.msgId}
                      isLastMsg={index === chatData.length - 1}
                      disableMsgOptions={true}
                      replyText={item.replyText}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <HomepagePlaceholder agentInfo={agentInfo as TAssistant} />
            )}
          </div>
        </div>
        <div className="w-full flex flex-col px-0 sm:px-1 pb-0 sm:pb-3 relative mx-auto sm:w-[400px] md:w-[500px] lg:w-[700px] xl:w-[900px] 2xl:w-[1000px]">
          <AISearchBox isCompareModel={false} />
        </div>
      </div>
    </div>
  );
}

export default App;
